import React, { useEffect } from 'react';
import { FaLayerGroup, FaTags, FaAsterisk } from 'react-icons/fa';
import { Select } from '../AdminForm';
import { ProductCategorySelectorProps } from './ProductFormTypes';
import { getValidationClassName } from './useProductFormValidation';

/**
 * Product Category Selector Component
 * Handles main category and subcategory selection with auto-selection logic
 */

const ProductCategorySelector: React.FC<ProductCategorySelectorProps> = ({
  product,
  onUpdate,
  mainCategories,
  filteredSubCategories,
  selectedMainCategory,
  onMainCategoryChange,
  validation,
  errors,
}) => {
  // Auto-select first subcategory when filtered categories change
  useEffect(() => {
    if (filteredSubCategories.length > 0 && !product.category_id) {
      onUpdate({ category_id: filteredSubCategories[0].id });
    }
  }, [filteredSubCategories, product.category_id, onUpdate]);

  const handleMainCategoryChange = (value: string) => {
    onUpdate({ main_category_id: value, category_id: '' });
    onMainCategoryChange(value);
  };

  const hasMainCategory = !!product.main_category_id;
  const hasSubCategory = !!product.category_id;

  return (
    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-5 rounded-xl border border-purple-100">
      <div className="space-y-4">
        {/* Section Header */}
        <div className="flex items-center gap-2 mb-2">
          <FaLayerGroup className="text-purple-500" />
          <h3 className="text-lg font-semibold text-gray-900">Kategori Seçimi</h3>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Ürününüzün hangi kategori altında görüneceğini belirleyin
        </p>

        {/* Main Category Selection */}
        <div className="relative">
          <Select
            label={
              <span className="flex items-center gap-1">
                Ana Kategori
                <FaAsterisk className="text-red-500 text-xs" />
              </span>
            }
            value={product.main_category_id || ''}
            onChange={handleMainCategoryChange}
            options={[
              { value: '', label: 'Ana kategori seçin...' },
              ...mainCategories.map(cat => ({
                value: cat.id,
                label: cat.name,
              })),
            ]}
            icon={<FaLayerGroup />}
            className={getValidationClassName(!!product.main_category_id, hasMainCategory)}
          />
        </div>

        {/* Subcategory Selection */}
        {filteredSubCategories.length > 0 && (
          <div className="relative">
            <Select
              label={
                <span className="flex items-center gap-1">
                  Alt Kategori
                  <FaAsterisk className="text-red-500 text-xs" />
                </span>
              }
              value={product.category_id || ''}
              onChange={value => onUpdate({ category_id: value })}
              options={[
                { value: '', label: 'Alt kategori seçin...' },
                ...filteredSubCategories.map(cat => ({
                  value: cat.id,
                  label: cat.name,
                })),
              ]}
              icon={<FaTags />}
              className={getValidationClassName(!!product.category_id, hasSubCategory)}
            />

            {/* Subcategory Helper Text */}
            <div className="mt-2 text-xs text-gray-500">
              {filteredSubCategories.length} alt kategori mevcut
            </div>
          </div>
        )}

        {/* Category Selection Status */}
        {hasMainCategory && !filteredSubCategories.length && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-700">
              Seçilen ana kategoride alt kategori bulunmuyor. Ürün doğrudan ana kategori altında
              listelenecek.
            </p>
          </div>
        )}

        {/* Validation Errors */}
        {errors.categories && (
          <p className="text-red-500 text-xs flex items-center gap-1">
            <FaAsterisk className="text-xs" />
            {errors.categories}
          </p>
        )}

        {/* Category Breadcrumb Preview */}
        {hasMainCategory && (
          <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="text-xs text-purple-600 font-medium mb-1">Kategori Yolu:</div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-purple-700">
                {mainCategories.find(cat => cat.id === product.main_category_id)?.name ||
                  'Ana Kategori'}
              </span>
              {hasSubCategory && (
                <>
                  <span className="text-purple-400">›</span>
                  <span className="text-purple-700">
                    {filteredSubCategories.find(cat => cat.id === product.category_id)?.name ||
                      'Alt Kategori'}
                  </span>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCategorySelector;
